import { Fragment } from 'react';
import { Box, Group, Stack, Text, Tooltip, UnstyledButton } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useMediaQuery } from '@mantine/hooks';

export default function PhaseBar({ phases = [], currentKey, completedKeys = [], onPhaseClick }) {
  const isNarrow = useMediaQuery('(max-width: 639px)', true);
  const completed = new Set(completedKeys);
  const currentPhase = phases.find((phase) => phase.key === currentKey) || null;

  if (!currentKey || !phases.length) return null;

  const timeRemaining = String(currentPhase?.timeRemaining || '').trim();

  return (
    <Stack gap={isNarrow ? 8 : 12}>
      <Group gap={0} wrap="nowrap" align="center">
        {phases.map((phase, index) => {
          const isCurrent = phase.key === currentKey;
          const isCompleted = completed.has(phase.key);
          const isUpcoming = !isCurrent && !isCompleted;
          const connectorComplete = index > 0 && completed.has(phases[index - 1].key);
          const circleSize = isCurrent ? (isNarrow ? 28 : 24) : (isNarrow ? 24 : 18);
          const isClickable = isCompleted && typeof onPhaseClick === 'function';

          const circle = (
            <Box
              style={{
                width: circleSize,
                height: circleSize,
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                border: isUpcoming ? '1px solid var(--mantine-color-sage-6)' : 'none',
                background: isUpcoming ? 'transparent' : 'var(--mantine-color-sage-6)',
                color: isUpcoming ? 'var(--mantine-color-sage-6)' : '#fff',
                transition: 'all 150ms ease',
              }}
            >
              {isNarrow ? (
                <Text size="xs" fw={700} c="inherit">
                  {index + 1}
                </Text>
              ) : (
                <>
                  {isCompleted && <IconCheck size={11} />}
                  {isCurrent && !isCompleted && (
                    <Box
                      style={{
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: '#fff',
                      }}
                    />
                  )}
                </>
              )}
            </Box>
          );

          const label = !isNarrow && (
            <Text
              size="xs"
              fw={isCurrent ? 700 : 500}
              style={{ color: isUpcoming ? 'var(--aviary-muted)' : 'var(--aviary-ink)' }}
            >
              {phase.label}
            </Text>
          );

          const stackContent = (
            <Stack
              gap={6}
              align="center"
              style={{ minWidth: isNarrow ? 28 : 74, flexShrink: 0 }}
            >
              {circle}
              {label}
            </Stack>
          );

          const phaseNode = isClickable ? (
            <Tooltip label={`Jump back to ${phase.label}`} withArrow openDelay={300}>
              <UnstyledButton
                onClick={() => onPhaseClick(phase.key)}
                style={{ cursor: 'pointer', borderRadius: 6 }}
              >
                {stackContent}
              </UnstyledButton>
            </Tooltip>
          ) : stackContent;

          return (
            <Fragment key={phase.key}>
              {index > 0 && (
                <Box
                  style={{
                    flex: 1,
                    height: 1,
                    background: connectorComplete ? 'var(--mantine-color-sage-6)' : 'var(--aviary-disabled)',
                    minWidth: isNarrow ? 10 : 20,
                  }}
                />
              )}
              {phaseNode}
            </Fragment>
          );
        })}
        {!isNarrow && timeRemaining && (
          <Text size="xs" style={{ color: 'var(--aviary-muted)', marginLeft: 12, flexShrink: 0 }}>
            {timeRemaining}
          </Text>
        )}
      </Group>

      {isNarrow && currentPhase && (
        <Group justify="space-between" align="center">
          <Text size="xs" fw={700} style={{ color: 'var(--aviary-ink)' }}>
            {currentPhase.label}
          </Text>
          {timeRemaining && (
            <Text size="xs" style={{ color: 'var(--aviary-muted)' }}>
              {timeRemaining}
            </Text>
          )}
        </Group>
      )}
    </Stack>
  );
}
